import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardSummary, ActivityItem } from '../types';
import { Card } from '@shared/components/ui/Card';
import * as Icons from 'lucide-react';
import { getStatusTone, type Tone } from '@shared/config/theme';
import {
  toneChipClasses,
  toneDotClasses,
  toneIconBgClasses,
  toneTextClasses,
} from '@shared/utils/statusTone';

/**
 * Tone for an activity row (contract §3). `getStatusTone` resolves every known
 * status; when it has no opinion (`neutral`) the list keeps its own accent —
 * backups read `warn`, restores read `accent` — so the two lists stay visually
 * distinguishable.
 */
function listTone(status: string | undefined, fallback: Tone): Tone {
  const tone = getStatusTone(status);
  return tone === 'neutral' ? fallback : tone;
}

type DashboardTranslator = (key: string) => string;

function translateActivityAction(action: string, t: DashboardTranslator): string {
  const keyByAction: Record<string, string> = {
    'backup completed': 'activity.backupCompleted',
    'backup failed': 'activity.backupFailed',
    'backup started': 'activity.backupStarted',
    'restore completed': 'activity.restoreCompleted',
    'restore failed': 'activity.restoreFailed',
    'restore requested': 'activity.restoreRequested',
  };
  const key = keyByAction[action.trim().toLowerCase()];
  return key ? t(key) : action;
}

function translateActivityStatus(status: string | undefined, t: DashboardTranslator): string {
  const keyByStatus: Record<string, string> = {
    completed: 'status.success',
    deleted: 'status.deleted',
    failed: 'status.failed',
    in_progress: 'status.inProgress',
    pending: 'status.pending',
    running: 'status.inProgress',
    'in progress': 'status.inProgress',
    success: 'status.success',
  };
  const key = status?.trim().toLowerCase();
  return key && keyByStatus[key] ? t(keyByStatus[key]) : status || t('status.unknown');
}

function ActivityStatusLabel({
  status,
  label,
  explanation,
  className,
}: {
  status: string | undefined;
  label: string;
  explanation: string;
  className: string;
}) {
  const isExpired = status?.trim().toLowerCase() === 'deleted';

  return (
    <span
      className={className}
      title={isExpired ? explanation : undefined}
      aria-label={isExpired ? `${label}. ${explanation}` : undefined}
      tabIndex={isExpired ? 0 : undefined}
    >
      {label}
    </span>
  );
}

function translateActivityType(type: string, t: DashboardTranslator): string {
  const normalized = type.trim().toLowerCase();
  if (normalized === 'backup') return t('activity.types.backup');
  if (normalized === 'restore') return t('activity.types.restore');
  return type;
}

function formatDateTime(timestamp: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(timestamp)
  );
}

/** Background classes of a tone chip, without its border. */
function chipBg(tone: Tone): string {
  return toneChipClasses[tone]
    .split(' ')
    .filter((c) => c.startsWith('bg-') || c.startsWith('dark:bg-'))
    .join(' ');
}

interface SimpleDashboardProps {
  dashboardData: DashboardSummary;
  setBackupNowModalOpen: (isOpen: boolean) => void;
}

const SimpleDashboard: React.FC<SimpleDashboardProps> = ({
  dashboardData,
  setBackupNowModalOpen,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const [activeTab, setActiveTab] = useState<'backups' | 'restores'>('backups');
  const [feedFilter, setFeedFilter] = useState<'all' | 'errors' | 'restores' | 'updates'>('all');
  const [selectedItem, setSelectedItem] = useState<ActivityItem | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Filter activities based on type
  const backupActivities = useMemo(() => {
    return dashboardData.recent_activity.filter(
      (item) =>
        item.type === 'BACKUP' ||
        item.action.toLowerCase().includes('backup') ||
        item.message.toLowerCase().includes('backup')
    );
  }, [dashboardData.recent_activity]);

  const restoreActivities = useMemo(() => {
    return dashboardData.recent_activity.filter(
      (item) =>
        item.type === 'RESTORE' ||
        item.action.toLowerCase().includes('restore') ||
        item.action.toLowerCase().includes('restauro') ||
        item.message.toLowerCase().includes('restore')
    );
  }, [dashboardData.recent_activity]);

  // Filter for live feed
  const filteredActivity = useMemo(() => {
    return dashboardData.recent_activity.filter((item) => {
      if (feedFilter === 'all') return true;
      if (feedFilter === 'errors')
        return item.status === 'FAILED' || item.status === 'offline' || item.status === 'critical';
      if (feedFilter === 'restores')
        return (
          item.type === 'RESTORE' ||
          item.action.toLowerCase().includes('restore') ||
          item.action.toLowerCase().includes('restauro')
        );
      if (feedFilter === 'updates')
        return item.status === 'online' || item.status === 'COMPLETED' || item.status === 'success';
      return true;
    });
  }, [dashboardData.recent_activity, feedFilter]);

  // Get last backup status for the alert
  const lastBackupStatus = dashboardData.backup_status;
  const hasLastBackup = lastBackupStatus.last_backup_time && lastBackupStatus.last_backup_status;

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* System Status Card */}
        <StatCard
          title={t('statusCards.system')}
          value={
            dashboardData.system_health.status === 'healthy'
              ? t('statusCards.healthy')
              : t('statusCards.issues')
          }
          subValue={`${dashboardData.system_health.agents_online}/${dashboardData.system_health.agents_total} ${t('statusCards.agents')}`}
          tone={dashboardData.system_health.status === 'healthy' ? 'ok' : 'warn'}
          icon={<Icons.Activity className="w-5 h-5" />}
          onClick={() => navigate('/agents')}
        />

        {/* Storage Card */}
        <StatCard
          title={t('statusCards.storage')}
          value={
            dashboardData.storage_usage.limit_gb > 0
              ? `${dashboardData.storage_usage.usage_percent.toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 0 })}%`
              : '—'
          }
          subValue={
            dashboardData.storage_usage.limit_gb > 0
              ? `${dashboardData.storage_usage.used_gb.toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 1 })} / ${dashboardData.storage_usage.limit_gb.toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 1 })} GB`
              : `${dashboardData.storage_usage.used_gb.toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 1 })} GB · ${t('statusCards.limitNotConfigured')}`
          }
          tone={
            dashboardData.storage_usage.usage_percent > 90
              ? 'danger'
              : dashboardData.storage_usage.usage_percent > 70
                ? 'warn'
                : 'ok'
          }
          icon={<Icons.HardDrive className="w-5 h-5" />}
          onClick={() => navigate('/storage-breakdown')}
        />

        {/* Configs Card */}
        <StatCard
          title={t('statusCards.configs')}
          value={dashboardData.backup_configs_count.toString()}
          subValue={t('statusCards.activeBackups', { count: dashboardData.backup_configs_count })}
          tone="info"
          icon={<Icons.Settings className="w-5 h-5" />}
          onClick={() => navigate('/backups/configs')}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Central Panel - History */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            {/* Panel Header */}
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
              {t('historyPanel.title', 'Histórico completo (backups e restaurações)')}
            </h2>

            {/* Quick Action Buttons */}
            <div className="grid grid-cols-2 gap-3 mt-6 mb-6">
              <ActionButton
                onClick={() => setBackupNowModalOpen(true)}
                icon={<Icons.Play className="w-4 h-4" />}
                label={t('actions.runBackup', 'Executar Backup')}
                variant="primary"
              />
              <ActionButton
                onClick={() => navigate('/system-logs')}
                icon={<Icons.FileText className="w-4 h-4" />}
                label={t('actions.viewSystemLogs', 'Ver logs do sistema')}
                variant="secondary"
              />
            </div>

            {/* Tabs */}
            <div className="flex border-b border-surface-border-light dark:border-surface-border-dark mb-4">
              <TabButton
                active={activeTab === 'backups'}
                onClick={() => setActiveTab('backups')}
                label={t('tabs.latestBackups', 'Últimos Backups')}
              />
              <TabButton
                active={activeTab === 'restores'}
                onClick={() => setActiveTab('restores')}
                label={t('tabs.latestRestores', 'Últimas restaurações')}
              />
            </div>

            {/* Tab Content */}
            <div className="space-y-3">
              {activeTab === 'backups' ? (
                <>
                  {/* Last Backup Status Alert */}
                  {hasLastBackup && lastBackupStatus.last_backup_status === 'failed' && (
                    <div className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${toneDotClasses.danger}`}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark uppercase tracking-wide">
                            {t('status.lastAttemptFailed', 'A ÚLTIMA TENTATIVA FALHOU')}
                          </p>
                          <p className="text-sm font-medium text-slate-900 dark:text-white mt-1">
                            {lastBackupStatus.last_backup_name}
                          </p>
                          <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                            {lastBackupStatus.last_backup_time
                              ? formatDateTime(
                                  lastBackupStatus.last_backup_time,
                                  i18n.resolvedLanguage
                                )
                              : '-'}
                          </p>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-bold rounded uppercase border ${toneChipClasses.danger}`}
                        >
                          {t('status.failed')}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Backup Activities List */}
                  {backupActivities.length > 0 ? (
                    backupActivities.slice(0, 5).map((item) => {
                      const tone = listTone(item.status, 'accent');
                      return (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${chipBg(tone)}`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-2 h-2 rounded-full flex-shrink-0 ${toneDotClasses[tone]}`}
                            />
                            <div>
                              <p className="text-sm font-medium text-slate-900 dark:text-white">
                                {translateActivityAction(item.action, t)}
                              </p>
                              <p className="text-xs text-text-muted-light dark:text-text-muted-dark truncate max-w-md">
                                {item.message}
                              </p>
                              <p className="text-[10px] text-text-subtle-light dark:text-text-subtle-dark mt-0.5">
                                {formatDateTime(item.timestamp, i18n.resolvedLanguage)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedItem(item);
                                setShowDetailsModal(true);
                              }}
                              className="p-1.5 hover:bg-surface-border-light dark:hover:bg-surface-border-dark rounded-lg transition-colors"
                              title={t('actions.viewDetails', 'Ver detalhes')}
                            >
                              <Icons.Eye
                                size={16}
                                className="text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300"
                              />
                            </button>
                            <ActivityStatusLabel
                              status={item.status}
                              className={`px-2 py-0.5 text-xs font-bold rounded uppercase ${toneChipClasses[tone]}`}
                              explanation={t('status.deletedExplanation')}
                              label={
                                item.status === 'COMPLETED' || item.status === 'success'
                                  ? t('status.success')
                                  : item.status === 'FAILED' || item.status === 'failed'
                                    ? t('status.failed')
                                    : translateActivityStatus(item.status, t)
                              }
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-text-subtle-light dark:text-text-subtle-dark">
                      <Icons.Database className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>{t('labels.noRecentEvents')}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {restoreActivities.length > 0 ? (
                    restoreActivities.slice(0, 5).map((item) => {
                      const tone = listTone(item.status, 'warn');
                      const isActive = !['FAILED', 'failed', 'COMPLETED', 'success'].includes(
                        item.status ?? ''
                      );
                      return (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${chipBg(tone)}`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-2 h-2 rounded-full flex-shrink-0 ${toneDotClasses[tone]} ${isActive ? 'animate-pulse' : ''}`}
                            />
                            <div>
                              <p className="text-sm font-medium text-slate-900 dark:text-white">
                                {translateActivityAction(item.action, t)}
                              </p>
                              <p className="text-xs text-text-muted-light dark:text-text-muted-dark truncate max-w-md">
                                {item.message}
                              </p>
                              <p className="text-[10px] text-text-subtle-light dark:text-text-subtle-dark mt-0.5">
                                {formatDateTime(item.timestamp, i18n.resolvedLanguage)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedItem(item);
                                setShowDetailsModal(true);
                              }}
                              className="p-1.5 hover:bg-surface-border-light dark:hover:bg-surface-border-dark rounded-lg transition-colors"
                              title={t('actions.viewDetails', 'Ver detalhes')}
                            >
                              <Icons.Eye
                                size={16}
                                className="text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300"
                              />
                            </button>
                            <ActivityStatusLabel
                              status={item.status}
                              className={`px-2 py-0.5 text-xs font-bold rounded uppercase ${toneChipClasses[tone]}`}
                              explanation={t('status.deletedExplanation')}
                              label={
                                item.status === 'COMPLETED' || item.status === 'success'
                                  ? t('status.success')
                                  : item.status === 'FAILED' || item.status === 'failed'
                                    ? t('status.failed')
                                    : translateActivityStatus(item.status, t)
                              }
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-text-subtle-light dark:text-text-subtle-dark">
                      <Icons.RotateCcw className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>{t('labels.noRecentRestores', 'Nenhuma restauração recente')}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>

          {/* Next Scheduled Panel */}
          {dashboardData.next_scheduled ? (
            <div className="bg-brand-600 dark:bg-brand-400 rounded-2xl shadow-lg p-6 text-on-accent-light dark:text-on-accent-dark relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Icons.Clock className="w-24 h-24" />
              </div>
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <Icons.Clock className="w-4 h-4" />
                  <span className="text-sm font-medium opacity-80">{t('nextScheduled.title')}</span>
                </div>
                <div className="text-4xl font-black mb-1">
                  {new Date(dashboardData.next_scheduled.time).toLocaleTimeString(
                    i18n.resolvedLanguage,
                    {
                      hour: '2-digit',
                      minute: '2-digit',
                    }
                  )}
                </div>
                <p className="text-sm font-medium mb-3">
                  {dashboardData.next_scheduled.config_name}
                </p>
                <span className="inline-block px-3 py-1 bg-surface-card-light/20 dark:bg-surface-card-dark/20 rounded-lg text-xs font-bold">
                  {new Date(dashboardData.next_scheduled.time).toLocaleDateString(
                    i18n.resolvedLanguage
                  )}
                </span>
              </div>
            </div>
          ) : (
            <div className="bg-surface-2-light dark:bg-surface-2-dark rounded-2xl p-6 text-text-muted-light dark:text-text-muted-dark">
              <div className="flex items-center gap-2 mb-2">
                <Icons.Clock className="w-4 h-4" />
                <span className="text-sm font-medium">{t('nextScheduled.title')}</span>
              </div>
              <p className="text-sm italic">{t('nextScheduled.noScheduled')}</p>
            </div>
          )}
        </div>

        {/* Right Panel - Live Feed */}
        <Card className="p-6">
          <h3 className="text-base font-bold text-slate-900 dark:text-white mb-4">
            {t('liveFeed.title')}
          </h3>

          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-1 mb-4">
            <FilterChip
              active={feedFilter === 'all'}
              onClick={() => setFeedFilter('all')}
              label={t('liveFeed.allEvents', 'Todos')}
            />
            <FilterChip
              active={feedFilter === 'errors'}
              onClick={() => setFeedFilter('errors')}
              label={t('liveFeed.criticalErrors', 'Erros')}
              variant="error"
            />
            <FilterChip
              active={feedFilter === 'restores'}
              onClick={() => setFeedFilter('restores')}
              label={t('liveFeed.requestedRestores', 'Restaurações')}
            />
            <FilterChip
              active={feedFilter === 'updates'}
              onClick={() => setFeedFilter('updates')}
              label={t('liveFeed.updates', 'Updates')}
              variant="success"
            />
          </div>

          {/* Events List */}
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {filteredActivity.length > 0 ? (
              filteredActivity.slice(0, 10).map((item) => {
                const isRestore =
                  item.type === 'RESTORE' || item.action.toLowerCase().includes('restore');
                return (
                  <div key={item.id} className="flex gap-3">
                    <div
                      className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${toneDotClasses[listTone(item.status, isRestore ? 'info' : 'accent')]}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {translateActivityAction(item.action, t)}
                      </p>
                      <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-0.5 truncate">
                        {item.message}
                      </p>
                      <p className="text-[10px] text-text-subtle-light dark:text-text-subtle-dark mt-1">
                        {new Date(item.timestamp).toLocaleTimeString(i18n.resolvedLanguage)}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-text-subtle-light dark:text-text-subtle-dark">
                <Icons.Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">{t('labels.noRecentEvents')}</p>
              </div>
            )}
          </div>

          {/* View Full Audit Link */}
          <button
            onClick={() => navigate('/audit-logs')}
            className="w-full mt-6 py-2.5 text-sm font-medium text-accent-text-light dark:text-accent-text-dark bg-accent-soft-light dark:bg-accent-soft-dark hover:bg-brand-100 dark:hover:bg-brand-900/40 rounded-lg transition-colors"
          >
            {t('liveFeed.viewFullAuditTrail')}
          </button>
        </Card>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedItem && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-activity-dialog-title"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-in fade-in duration-300"
        >
          <Card className="max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto animate-in zoom-in-95 duration-300 shadow-elevated">
            <div className="p-6 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      toneIconBgClasses[
                        listTone(
                          selectedItem.status,
                          selectedItem.type === 'RESTORE' ||
                            selectedItem.action.toLowerCase().includes('restore')
                            ? 'accent'
                            : 'ok'
                        )
                      ]
                    }`}
                  >
                    {selectedItem.type === 'RESTORE' ||
                    selectedItem.action.toLowerCase().includes('restore') ? (
                      <Icons.RotateCcw size={20} />
                    ) : (
                      <Icons.Database size={20} />
                    )}
                  </div>
                  <div>
                    <h3
                      id="dashboard-activity-dialog-title"
                      className="text-lg font-black text-slate-900 dark:text-white"
                    >
                      {selectedItem.type === 'RESTORE' ||
                      selectedItem.action.toLowerCase().includes('restore')
                        ? t('modal.restoreDetails', 'Detalhes do Restauro')
                        : t('modal.backupDetails', 'Detalhes do Backup')}
                    </h3>
                    <ActivityStatusLabel
                      status={selectedItem.status}
                      label={translateActivityStatus(selectedItem.status, t)}
                      explanation={t('status.deletedExplanation')}
                      className={`text-xs font-bold uppercase ${toneTextClasses[listTone(selectedItem.status, 'warn')]}`}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  aria-label={tCommon('actions.close')}
                  onClick={() => setShowDetailsModal(false)}
                  className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl transition-colors hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                >
                  <Icons.X
                    size={18}
                    className="text-text-subtle-light dark:text-text-subtle-dark"
                  />
                </button>
              </div>

              {/* Details */}
              <div className="space-y-3 pt-2">
                <DetailRow
                  label={t('modal.action', 'Ação')}
                  value={translateActivityAction(selectedItem.action, t)}
                />
                <DetailRow label={t('modal.message', 'Mensagem')} value={selectedItem.message} />
                <DetailRow
                  label={t('modal.timestamp', 'Data/Hora')}
                  value={formatDateTime(selectedItem.timestamp, i18n.resolvedLanguage)}
                />
                <DetailRow
                  label={t('modal.type', 'Tipo')}
                  value={translateActivityType(selectedItem.type, t)}
                />
                <DetailRow label={t('modal.id', 'ID')} value={selectedItem.id} mono />

                {/* Error Message for Failed Jobs */}
                {(selectedItem.status === 'FAILED' || selectedItem.status === 'failed') &&
                  selectedItem.error_message && (
                    <div className="mt-4 p-3 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-lg">
                      <span className="text-[10px] font-black text-state-danger-light dark:text-state-danger-dark uppercase tracking-wider">
                        {t('modal.errorMessage', 'Erro')}
                      </span>
                      <p className="text-sm font-medium text-state-danger-light dark:text-state-danger-dark mt-1 break-all">
                        {selectedItem.error_message}
                      </p>
                    </div>
                  )}
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-surface-border-light dark:border-surface-border-dark flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="min-h-11 rounded-lg bg-surface-2-light px-4 font-medium text-slate-700 transition-colors hover:bg-surface-border-light dark:bg-surface-2-dark dark:text-slate-300 dark:hover:bg-surface-border-dark"
                >
                  {t('modal.close', 'Fechar')}
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
function StatCard({
  title,
  value,
  subValue,
  tone,
  icon,
  onClick,
}: {
  title: string;
  value: string;
  subValue: string;
  tone: Tone;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={`${title}: ${value}. ${subValue}`}
      className={`text-left p-5 rounded-xl border ${toneChipClasses[tone]} transition-all hover:shadow-md hover:scale-[1.01] active:scale-[0.99]`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider mb-1">{title}</p>
          <p className="text-2xl font-black text-slate-900 dark:text-white mb-1">{value}</p>
          <p className="text-xs text-text-muted-light dark:text-text-muted-dark">{subValue}</p>
        </div>
        <div className={`p-2 rounded-lg ${toneIconBgClasses[tone]}`}>{icon}</div>
      </div>
    </button>
  );
}

// Action Button Component
function ActionButton({
  onClick,
  icon,
  label,
  variant = 'secondary',
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  variant?: 'primary' | 'secondary';
}) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-2 p-3 rounded-lg text-center transition-all active:scale-95 ${
        variant === 'primary'
          ? 'bg-brand-600 hover:bg-brand-700 text-on-accent-light shadow-sm dark:bg-brand-400 dark:text-on-accent-dark dark:hover:bg-brand-300'
          : 'bg-surface-2-light dark:bg-surface-2-dark hover:bg-surface-border-light dark:hover:bg-surface-border-dark text-slate-700 dark:text-slate-200'
      }`}
    >
      <div
        className={
          variant === 'primary' ? 'text-current' : 'text-text-muted-light dark:text-text-muted-dark'
        }
      >
        {icon}
      </div>
      <span className="text-xs font-medium leading-tight">{label}</span>
    </button>
  );
}

// Detail Row Component
function DetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-wider">
        {label}
      </span>
      <span
        className={`text-sm font-medium text-slate-900 dark:text-slate-100 break-all ${mono ? 'font-mono text-xs' : ''}`}
      >
        {value || '-'}
      </span>
    </div>
  );
}

// Tab Button Component
function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-brand-600 text-accent-text-light dark:border-brand-400 dark:text-accent-text-dark'
          : 'border-transparent text-text-muted-light dark:text-text-muted-dark hover:text-slate-700 dark:hover:text-slate-200'
      }`}
    >
      {label}
    </button>
  );
}

// Filter Chip Component
function FilterChip({
  active,
  onClick,
  label,
  variant = 'default',
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  variant?: 'default' | 'error' | 'success';
}) {
  const variantClasses = {
    default: active
      ? 'bg-accent-soft-light dark:bg-accent-soft-dark text-accent-text-light dark:text-accent-text-dark'
      : 'bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark hover:bg-surface-border-light dark:hover:bg-surface-border-dark',
    error: active
      ? toneChipClasses.danger
      : 'bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark hover:bg-state-danger-bg-light dark:hover:bg-state-danger-bg-dark',
    success: active
      ? toneChipClasses.ok
      : 'bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark hover:bg-state-ok-bg-light dark:hover:bg-state-ok-bg-dark',
  };

  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`min-h-11 px-3 py-2 text-xs font-medium rounded-full transition-colors ${variantClasses[variant]}`}
    >
      {label}
    </button>
  );
}

export default SimpleDashboard;
